// IndexedDB wrapper for offline-first data persistence
const DB_NAME = 'cigartrack-offline'
const DB_VERSION = 2

export const STORES = {
  CIGARS: 'cigars',
  SESSIONS: 'sessions',
  OFFLINE_QUEUE: 'offline_queue',
  SYNC_CONFLICTS: 'sync_conflicts',
  SYNC_HISTORY: 'sync_history',
} as const

export interface SyncConflict {
  id: string
  queueItemId: string
  operation: 'CREATE' | 'UPDATE' | 'DELETE'
  entity: string
  localPayload: unknown
  serverError: string
  timestamp: number
  resolved: boolean
}

export interface SyncHistoryEntry {
  id: string
  timestamp: number
  itemsSynced: number
  itemsFailed: number
  conflicts: number
  durationMs: number
}

export interface OfflineQueueItem {
  id: string
  operation: 'CREATE' | 'UPDATE' | 'DELETE'
  entity: 'cigar' | 'session' | 'humidor'
  payload: unknown
  timestamp: number
  retryCount: number
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      if (!db.objectStoreNames.contains(STORES.CIGARS)) {
        const cigarStore = db.createObjectStore(STORES.CIGARS, { keyPath: 'id' })
        cigarStore.createIndex('updatedAt', 'updatedAt', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
        const sessionStore = db.createObjectStore(STORES.SESSIONS, { keyPath: 'id' })
        sessionStore.createIndex('smokedDate', 'smokedDate', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id' })
        queueStore.createIndex('timestamp', 'timestamp', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.SYNC_CONFLICTS)) {
        const conflictStore = db.createObjectStore(STORES.SYNC_CONFLICTS, { keyPath: 'id' })
        conflictStore.createIndex('timestamp', 'timestamp', { unique: false })
        conflictStore.createIndex('resolved', 'resolved', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.SYNC_HISTORY)) {
        const historyStore = db.createObjectStore(STORES.SYNC_HISTORY, { keyPath: 'id' })
        historyStore.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result)
    }

    request.onerror = (event) => {
      dbPromise = null
      reject((event.target as IDBOpenDBRequest).error)
    }
  })
  return dbPromise
}

function generateId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).slice(2, 9)
  return `offline_${timestamp}_${random}`
}

export async function putRecord<T extends { id: string }>(
  storeName: string,
  record: T
): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    const request = store.put(record)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function putRecords<T extends { id: string }>(
  storeName: string,
  records: T[]
): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    if (records.length === 0) { resolve(); return }
    records.forEach((record) => {
      const request = store.put(record)
      request.onerror = () => reject(request.error)
    })
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export async function getRecord<T>(
  storeName: string,
  id: string
): Promise<T | undefined> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    const request = store.get(id)
    request.onsuccess = () => resolve(request.result as T | undefined)
    request.onerror = () => reject(request.error)
  })
}

export async function getAllRecords<T>(storeName: string): Promise<T[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    const request = store.getAll()
    request.onsuccess = () => resolve(request.result as T[])
    request.onerror = () => reject(request.error)
  })
}

export async function deleteRecord(storeName: string, id: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    const request = store.delete(id)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function clearStore(storeName: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    const request = store.clear()
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Queue operations
export async function enqueueOperation(
  operation: OfflineQueueItem['operation'],
  entity: OfflineQueueItem['entity'],
  payload: unknown
): Promise<string> {
  const id = generateId()
  const item: OfflineQueueItem = {
    id,
    operation,
    entity,
    payload,
    timestamp: Date.now(),
    retryCount: 0,
  }
  await putRecord(STORES.OFFLINE_QUEUE, item)
  return id
}

export async function getQueueItems(): Promise<OfflineQueueItem[]> {
  const items = await getAllRecords<OfflineQueueItem>(STORES.OFFLINE_QUEUE)
  const opPriority = { CREATE: 0, UPDATE: 1, DELETE: 2 }
  return items.sort((a, b) => {
    const opDiff = opPriority[a.operation] - opPriority[b.operation]
    if (opDiff !== 0) return opDiff
    return a.timestamp - b.timestamp
  })
}

export async function removeQueueItem(id: string): Promise<void> {
  return deleteRecord(STORES.OFFLINE_QUEUE, id)
}

export async function incrementRetryCount(id: string): Promise<void> {
  const item = await getRecord<OfflineQueueItem>(STORES.OFFLINE_QUEUE, id)
  if (item) {
    await putRecord(STORES.OFFLINE_QUEUE, { ...item, retryCount: item.retryCount + 1 })
  }
}

// Conflict operations
export async function addConflict(conflict: Omit<SyncConflict, 'id'>): Promise<string> {
  const id = generateId()
  await putRecord(STORES.SYNC_CONFLICTS, { ...conflict, id })
  return id
}

export async function getUnresolvedConflicts(): Promise<SyncConflict[]> {
  const all = await getAllRecords<SyncConflict>(STORES.SYNC_CONFLICTS)
  return all.filter((c) => !c.resolved).sort((a, b) => b.timestamp - a.timestamp)
}

export async function resolveConflict(id: string): Promise<void> {
  const item = await getRecord<SyncConflict>(STORES.SYNC_CONFLICTS, id)
  if (item) {
    await putRecord(STORES.SYNC_CONFLICTS, { ...item, resolved: true })
  }
}

// Sync history operations
export async function addSyncHistory(entry: Omit<SyncHistoryEntry, 'id'>): Promise<void> {
  const id = generateId()
  await putRecord(STORES.SYNC_HISTORY, { ...entry, id })
}

export async function getSyncHistory(limit = 20): Promise<SyncHistoryEntry[]> {
  const all = await getAllRecords<SyncHistoryEntry>(STORES.SYNC_HISTORY)
  return all.sort((a, b) => b.timestamp - a.timestamp).slice(0, limit)
}

// Reset for testing
export function resetDB(): void {
  dbPromise = null
}
